import React from 'react';
import { Modal, ModalProps, Button, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import classes from './ImportantNoticeModal.module.scss';

const { Title, Text } = Typography;

interface ImportantNoticeModalProps extends Omit<ModalProps, 'children' | 'onCancel'> {
  /** Callback fired when user acknowledges and submits */
  onSubmit: () => void | Promise<void>;
  /** Loading state for submit button */
  loading?: boolean;
  /** Error message to display */
  error?: string;
  /** Modal width in pixels */
  width?: number;
}

/**
 * Modal component for displaying important payment verification notices.
 * This modal cannot be dismissed and requires user acknowledgment.
 * 
 * @param {boolean} open - Controls modal visibility
 * @param {Function} onSubmit - Callback fired when user acknowledges and submits
 * @param {boolean} [loading=false] - Loading state for submit button
 * @param {string} [error] - Error message to display
 * @param {number} [width=400] - Modal width in pixels
 * 
 * @example
 * ```tsx
 * <ImportantNoticeModal
 *   open={isOpen}
 *   onSubmit={handleSubmit}
 *   loading={isLoading}
 *   error={errorMessage}
 *   width={500}
 * />
 * ```
 */
const ImportantNoticeModal: React.FC<ImportantNoticeModalProps> = ({
  onSubmit,
  loading = false,
  error,
  width = 400,
  ...props
}) => {
  const { formatMessage: t } = useIntl();

  return (
    <Modal
      {...props}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      width={width}
      className={classes.modal}
      data-testid="important-notice-modal"
      aria-labelledby="important-notice-title"
      aria-describedby="important-notice-content"
    >
      <div className={classes.container}>
        <div className={classes.iconContainer}>
          <ExclamationCircleOutlined className={classes.icon} />
        </div>
        
        <Title level={4} className={classes.title} id="important-notice-title">
          {t({ id: 'importantNotice.title', defaultMessage: 'Important Notice' })}
        </Title>
        
        <Text className={classes.content} id="important-notice-content">
          <FormattedMessage
            id="importantNotice.content"
            defaultMessage="Please ensure that your <strong>first top-up</strong> is made from an account in <strong>your own name</strong>. Once your account has been verified with an initial personal transaction, <strong>future top-ups from third parties</strong> (such as friends, family, or employers) will be accepted. Initial deposits from third-party sources will be rejected and may incur a fee."
            values={{
              strong: (chunks) => <strong>{chunks}</strong>
            }}
          />
        </Text>

        {error && (
          <Text className={classes.error} role="alert" aria-live="polite">
            {error}
          </Text>
        )}
        
        <Button
          block
          type="primary"
          loading={loading}
          className={classes.submitButton}
          onClick={onSubmit}
          data-testid="important-notice-submit"
        >
          {t({ id: 'buttons.submit', defaultMessage: 'Submit' })}
        </Button>
      </div>
    </Modal>
  );
};

export default React.memo(ImportantNoticeModal);
export type { ImportantNoticeModalProps };

// Translation keys for reusability
export const IMPORTANT_NOTICE_I18N_KEYS = {
  TITLE: 'importantNotice.title',
  CONTENT: 'importantNotice.content',
  SUBMIT: 'buttons.submit',
} as const;
